import { useEffect, useState } from 'react';
import {
  errorApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import Skeleton from '@mui/material/Skeleton';
import {
  useDevToolsHubCopilotBudget,
  type CopilotBudget as CopilotBudgetApiResult,
} from '../../../hooks/use-devtools-hub-copilot-budget';
import { parseEntityRef } from '@backstage/catalog-model';
import { Flex, Grid, Text } from '@backstage/ui';
import { CopilotBudgetCard } from './copilot-budget-card';
import {
  CopilotBudgetProgress,
  getProgressColor,
} from './copilot-budget-progress';

const MAX_PERCENT = 100;

const formatCost = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);

type BudgetUsageResult = {
  percentUsed: number;
  budgetUsed: number;
  budgetLimit: number;
};

/**
 * Derives usage metrics from the backend Copilot budget payload.
 */
const getBudgetUsage = (budget: CopilotBudgetApiResult): BudgetUsageResult => {
  if (budget.budgetLimit <= 0) {
    return {
      percentUsed: 0,
      budgetUsed: budget.budgetUsed,
      budgetLimit: budget.budgetLimit,
    };
  }

  const percentUsed = Math.round(
    (budget.budgetUsed / budget.budgetLimit) * 100,
  );

  return {
    percentUsed: Math.min(MAX_PERCENT, Math.max(0, percentUsed)),
    budgetUsed: budget.budgetUsed,
    budgetLimit: budget.budgetLimit,
  };
};

type CopilotBudgetState =
  | { status: 'loading' }
  | { status: 'disabled' }
  | { status: 'error' }
  | {
      status: 'ready';
      percentUsed: number;
      budgetUsed: number;
      budgetLimit: number;
    };

const GENERIC_ERROR_MESSAGE =
  'Could not load your GitHub Copilot budget usage. Please try again later.';

const getBudgetUsageHeadline = ({
  hasBudgetAllocated,
  showRawCredits,
  percentUsed,
  budgetUsed,
}: {
  hasBudgetAllocated: boolean;
  showRawCredits: boolean;
  percentUsed: number;
  budgetUsed: number;
}): { primaryText: string; secondaryText: string } => {
  if (!hasBudgetAllocated) {
    return { primaryText: 'N/A', secondaryText: 'no budget allocated' };
  }
  if (showRawCredits) {
    return {
      primaryText: formatCost(budgetUsed),
      secondaryText: 'Cost',
    };
  }
  return { primaryText: `${percentUsed}%`, secondaryText: 'used' };
};

export const CopilotBudget = () => {
  const identityApi = useApi(identityApiRef);
  const errorApi = useApi(errorApiRef);
  const fetchCopilotBudget = useDevToolsHubCopilotBudget();

  const [state, setState] = useState<CopilotBudgetState>({ status: 'loading' });

  useEffect(() => {
    let active = true;

    const fetchCopilotBudgetUsage = async () => {
      try {
        setState({ status: 'loading' });

        const { userEntityRef } = await identityApi.getBackstageIdentity();
        const { name } = parseEntityRef(userEntityRef);
        const response = await fetchCopilotBudget(name);

        if (!active) {
          return;
        }

        if (!response) {
          setState({ status: 'disabled' });
          return;
        }

        const budgetUsage = getBudgetUsage(response);

        setState({
          status: 'ready',
          percentUsed: budgetUsage.percentUsed,
          budgetUsed: budgetUsage.budgetUsed,
          budgetLimit: budgetUsage.budgetLimit,
        });
      } catch (err) {
        // Report the underlying detail for debugging, but show the user a
        // clean, generic message that doesn't leak internals.
        errorApi.post(err instanceof Error ? err : new Error(String(err)));

        if (!active) {
          return;
        }

        setState({ status: 'error' });
      }
    };

    fetchCopilotBudgetUsage();

    return () => {
      active = false;
    };
  }, [fetchCopilotBudget, identityApi, errorApi]);

  // Don't show the widget when Copilot is not enabled for the user.
  if (state.status === 'disabled') {
    return null;
  }

  if (state.status === 'loading') {
    return (
      <CopilotBudgetCard>
        <>
          <Skeleton variant="text" width="100%" height={24} />
          <Text variant="body-small" color="secondary">
            Loading Copilot usage...
          </Text>
        </>
      </CopilotBudgetCard>
    );
  }

  if (state.status === 'error') {
    return (
      <CopilotBudgetCard>
        <Text variant="body-small">{GENERIC_ERROR_MESSAGE}</Text>
      </CopilotBudgetCard>
    );
  }

  const hasBudgetAllocated = state.budgetLimit > 0;
  const progressValue = hasBudgetAllocated ? state.percentUsed : 0;

  const { primaryText } = getBudgetUsageHeadline({
    hasBudgetAllocated,
    showRawCredits: false,
    percentUsed: state.percentUsed,
    budgetUsed: state.budgetUsed,
  });

  return (
    <CopilotBudgetCard>
      <Grid.Root columns="3" gap="2" mt="2" mb="3">
        <Grid.Item style={{ borderRight: '1px solid var(--bui-border-1)' }}>
          <Flex direction="column" align="center" gap="1">
            <Text variant="body-medium" weight="bold">
              Budget Used
            </Text>
            <Text variant="title-small" weight="bold">
              ${Math.round(state.budgetUsed)}
            </Text>
          </Flex>
        </Grid.Item>
        <Grid.Item>
          <Flex direction="column" align="center" gap="1">
            <Text variant="body-medium" weight="bold">
              Allocated Budget
            </Text>
            <Text variant="title-small" weight="bold">
              ${Math.round(state.budgetLimit)}
            </Text>
          </Flex>
        </Grid.Item>
        <Grid.Item style={{ borderLeft: '1px solid var(--bui-border-1)' }}>
          <Flex direction="column" align="center" gap="1">
            <Text variant="body-medium" weight="bold">
              Usage
            </Text>
            <Text
              variant="title-small"
              weight="bold"
              style={{ color: getProgressColor(progressValue) }}
            >
              {primaryText}
            </Text>
          </Flex>
        </Grid.Item>
      </Grid.Root>

      <CopilotBudgetProgress value={progressValue} />
    </CopilotBudgetCard>
  );
};

export { getProgressColor, formatCost };
